/**
 * The words of the media library, the publication calendar and the redirects
 * (0.9.0). Kept apart from dict.ts only to keep that file readable; they are
 * spread into the same two trees, so `dict.test.ts` checks them like the rest.
 */
import type { DictNode } from './dict.js'

export const libraryIt: DictNode = {
  media: {
    title: 'Libreria media',
    subtitle: 'Immagini, PDF e video di {{site}}: caricali una volta e usali in tutti i contenuti.',
    search: 'Cerca nella libreria',
    searchPlaceholder: 'Cerca per nome o percorso',
    filters: { kind: 'Tipo di file', status: 'Stato' },
    kinds: {
      all: 'Tutti',
      image: 'Immagini',
      document: 'Documenti',
      video: 'Video',
      imageOne: 'Immagine',
      documentOne: 'Documento',
      videoOne: 'Video',
    },
    statuses: {
      active: 'In uso',
      archived: 'Archiviati',
      all: 'Tutti',
      activeOne: 'In uso',
      archivedOne: 'Archiviato',
    },
    loadError: 'Non riesco a leggere la libreria',
    retry: 'Riprova',
    empty: 'La libreria è vuota',
    emptyHelp: 'Trascina qui sopra le immagini o premi «Scegli file». Poi le trovi nel campo immagine di ogni contenuto.',
    emptyReadOnly: 'Nessun file caricato per questo sito. Il tuo ruolo può vedere la libreria ma non caricare.',
    emptyFiltered: 'Nessun file corrisponde',
    emptyFilteredHelp: 'Prova con un’altra parola o togli i filtri.',
    clearFilters: 'Togli i filtri',
    count: '{{count}} file, {{size}} in tutto',
    noAlt: 'Manca il testo alternativo',
    upload: {
      drop: 'Trascina qui i file da caricare',
      limits: 'PNG, JPG, GIF, WebP, SVG, PDF e MP4 fino a 10 MB. Anche più file insieme.',
      choose: 'Scegli file',
      fileNamePlaceholder: 'nome del file',
      progress: 'Caricamento di {{name}}',
      waiting: 'In coda',
      duplicate: 'C’era già nella libreria: uso quello, {{path}}',
      clear: 'Pulisci l’elenco dei caricamenti',
      problems: {
        empty: 'Il file è vuoto.',
        tooLarge: 'Pesa {{size}}: il limite è 10 MB. Riducilo (per esempio in WebP) e riprova.',
        unsupported: 'Questo tipo di file non si carica: la libreria accetta PNG, JPG, GIF, WebP, SVG, PDF e MP4.',
      },
    },
    reason: {
      upload: 'Caricato «{{name}}» nella libreria da Migliore CMS',
      uploadForField: 'Caricato «{{name}}» per il campo «{{field}}» da Migliore CMS',
      update: 'Aggiornato il file «{{name}}» della libreria da Migliore CMS',
      archive: 'Archiviato il file «{{name}}» della libreria da Migliore CMS',
      restore: 'Ripristinato il file «{{name}}» della libreria da Migliore CMS',
    },
    detail: {
      title: 'File della libreria',
      loadError: 'Non riesco ad aprire il file',
      saved: 'File aggiornato',
      archived: 'File archiviato: resta fuori dalla prossima pubblicazione',
      restored: 'File ripristinato',
      nameRequired: 'Il nome non può essere vuoto.',
      copied: 'Copiato: {{text}}',
      copyFailed: 'Il browser non ha permesso di copiare: seleziona l’indirizzo e copialo a mano.',
      copyPath: 'Copia percorso',
      copyUrl: 'Copia indirizzo pubblico',
      openFile: 'Apri il file',
      status: 'Stato',
      publicPath: 'Percorso nel sito',
      type: 'Tipo',
      dimensions: 'Dimensioni',
      size: 'Peso',
      version: 'Versione',
      notStored: 'Questo file è stato registrato prima che la libreria conservasse i file: esiste solo sul sito pubblicato. L’anteprima viene da lì.',
      archivedTitle: 'File archiviato',
      archivedHelp: 'Non compare più nella libreria né nella prossima pubblicazione. Il file resta conservato: ripristinalo per usarlo di nuovo.',
      name: 'Nome',
      path: 'Percorso',
      pathHelp: 'Sotto media/, con la stessa estensione. È l’indirizzo che le pagine usano.',
      pathChangeWarning: 'Attenzione: le pagine che usano il percorso attuale vanno cambiate anche loro, altrimenti mostrano un’immagine mancante.',
      alt: 'Testo alternativo',
      altHelp: 'Descrive l’immagine a chi non la vede e ai motori di ricerca. Una frase breve per ogni lingua del sito.',
      altFor: 'Testo alternativo ({{locale}})',
      archive: 'Archivia',
      archiveTitle: 'Archiviare il file?',
      archiveMessage: 'Il file {{path}} esce dalla libreria e dalla prossima pubblicazione. Le pagine che lo usano mostreranno un’immagine mancante. Si può ripristinare.',
      restore: 'Ripristina',
    },
    picker: {
      title: 'Scegli dalla libreria',
      titleFor: 'Scegli un file per «{{field}}»',
      hint: 'Tocca un file per selezionarlo, doppio clic per usarlo subito.',
      selected: 'Selezionato: {{name}}',
      use: 'Usa questo file',
    },
    field: {
      choose: 'Scegli dalla libreria',
      upload: 'Carica',
      clear: 'Togli il file',
      archived: 'archiviato: non sarà pubblicato',
      outsideLibrary: 'Questo indirizzo non è nella libreria: va bene, ma l’anteprima dipende dal sito pubblicato.',
    },
  },
  schedule: {
    title: 'Calendario pubblicazioni',
    subtitle: 'I contenuti pubblicati con una data futura. Alla data il sito si ripubblica da solo, una volta sola.',
    openContents: 'Contenuti pubblicati',
    loadError: 'Non riesco a leggere il calendario',
    deliveryOffTitle: 'La distribuzione del sito è spenta',
    deliveryOff: 'Alla data non succede niente: i contenuti restano fuori finché non la riaccendi dalle impostazioni.',
    openSettings: 'Apri impostazioni',
    next: 'Prossima uscita: {{when}} ({{relative}})',
    nextHelp: 'A quell’ora il CMS ripubblica {{domain}} con il contenuto nuovo, senza che nessuno debba premere «Pubblica sito».',
    truncated: 'Mostro le prime {{shown}} uscite su {{total}}.',
    views: 'Viste del calendario',
    week: 'Settimana',
    list: 'Elenco',
    empty: 'Nessuna uscita programmata',
    emptyHelp: 'Apri un contenuto, scegli una data futura nel riquadro «Programmazione» e segnalo come «Pubblicato»: comparirà qui.',
    weekOf: '{{from}} – {{to}}',
    previousWeek: 'Settimana precedente',
    nextWeek: 'Settimana successiva',
    today: 'Oggi',
    nothing: 'Niente',
    otherWeeks: 'Altre {{count}} uscite in altre settimane.',
    goToNext: 'Vai alla prossima',
    entryMeta: 'Alle {{time}} · {{locale}} · /{{slug}}',
    reason: {
      set: 'Data di pubblicazione impostata al {{date}} da Migliore CMS',
      clear: 'Data di pubblicazione tolta da Migliore CMS',
    },
    card: {
      title: 'Programmazione',
      date: 'Data e ora di pubblicazione',
      help: 'Nel tuo fuso orario. Una data futura tiene il contenuto fuori dal sito fino a quel momento.',
      blocked: 'Salva prima le altre modifiche: la data si salva da sola.',
      none: 'Nessuna data: il contenuto esce alla prossima pubblicazione del sito.',
      scheduled: 'Esce {{when}}',
      past: 'Data di pubblicazione: {{when}}.',
      waiting: 'Data futura, ma non ancora «Pubblicato»',
      waitingHelp: 'Segna il contenuto come «Pubblicato»: uscirà alla data scelta, non prima.',
      save: 'Salva data',
      clear: 'Togli data',
      saved: 'Data salvata',
      cleared: 'Data tolta',
      conflict: 'Il contenuto è cambiato nel frattempo: l’ho ricaricato. La data scelta è ancora qui, premi di nuovo «Salva data».',
      openCalendar: 'Apri il calendario',
    },
  },
  redirects: {
    title: 'Redirect',
    subtitle: 'Indirizzi vecchi che portano a quelli nuovi, per non perdere visite e posizioni. Valgono dalla prossima pubblicazione del sito.',
    new: 'Nuovo redirect',
    countStatic: '{{count}} di {{max}} fissi',
    countDynamic: '{{count}} di {{max}} con segnaposto',
    publishHint: 'Limiti di Cloudflare Pages.',
    nearLimitTitle: 'Quasi al limite di Cloudflare Pages',
    nearLimit: 'Restano {{staticLeft}} redirect fissi e {{dynamicLeft}} con segnaposto. Oltre, Pages ignorerebbe gli altri: togli quelli che nessuno visita più.',
    collapsedTitle: 'Catena compressa',
    collapsed: '{{typed}} era a sua volta un redirect: {{from}} ora porta direttamente a {{stored}}, in un passaggio solo.',
    loadError: 'Non riesco a leggere i redirect',
    search: 'Cerca nei redirect',
    searchPlaceholder: 'Cerca un indirizzo vecchio o nuovo',
    empty: 'Nessun redirect',
    emptyHelp: 'Quando un indirizzo cambia, aggiungi qui il vecchio e il nuovo: chi arriva da Google o da un link salvato trova la pagina giusta.',
    emptyFiltered: 'Nessun redirect contiene «{{q}}»',
    emptyFilteredHelp: 'La ricerca guarda sia l’indirizzo vecchio sia quello nuovo.',
    clearSearch: 'Togli la ricerca',
    status: {
      301: '301 permanente',
      302: '302 temporaneo',
    },
    statusHelp: {
      301: 'Per sempre: i motori di ricerca passano la posizione al nuovo indirizzo. La scelta giusta quasi sempre.',
      302: 'Per un periodo: i motori di ricerca tengono il vecchio indirizzo. Per campagne e pagine momentanee.',
    },
    dynamic: 'Con segnaposto',
    dynamicHelp: 'Con * o :nome vale per tanti indirizzi insieme (per esempio /blog/* → /articoli/:splat). Pages ne accetta al massimo 100.',
    edit: 'Modifica',
    actionsFor: 'Azioni per {{from}}',
    tryOld: 'Prova il vecchio indirizzo',
    tryOldHelp: 'Sul sito pubblicato',
    remove: 'Rimuovi',
    removeTitle: 'Rimuovere il redirect?',
    removeMessage: 'Dopo la prossima pubblicazione {{from}} non porterà più a {{to}}: chi lo apre troverà una pagina inesistente.',
    wwwNote: 'Il passaggio da www al dominio principale non si imposta qui: è una regola del dominio su Cloudflare.',
    newTitle: 'Nuovo redirect',
    editTitle: 'Modifica redirect',
    refused: 'Il redirect non è stato salvato',
    from: 'Indirizzo vecchio',
    fromHelp: 'Il percorso che non esiste più, senza dominio: /vecchio-articolo. Puoi incollare l’indirizzo completo, tengo solo il percorso.',
    fromChanged: 'Cambi l’indirizzo vecchio: {{old}} verrà rimosso e sostituito da questo.',
    fromTaken: 'Esiste già un redirect da questo indirizzo: salvando lo sostituisci.',
    to: 'Indirizzo nuovo',
    toHelp: 'Un percorso del sito (/nuovo-articolo) o un indirizzo completo https://…',
    type: 'Tipo',
    chainHelp: 'Se l’indirizzo nuovo è a sua volta un redirect, il CMS salva direttamente la destinazione finale. Un giro che torna al punto di partenza viene rifiutato.',
    added: 'Redirect da {{from}} aggiunto',
    updated: 'Redirect da {{from}} aggiornato',
    removed: 'Redirect da {{from}} rimosso',
    reason: {
      add: 'Aggiunto il redirect {{from}} → {{to}} da Migliore CMS',
      update: 'Aggiornato il redirect {{from}} → {{to}} da Migliore CMS',
      remove: 'Rimosso il redirect da {{from}} da Migliore CMS',
    },
    problems: {
      fromMissing: 'Scrivi l’indirizzo vecchio.',
      fromHome: 'La home non si può reindirizzare: ogni altro indirizzo perderebbe la strada di casa.',
      fromSpaces: 'L’indirizzo vecchio non può contenere spazi.',
      fromQuery: 'Solo il percorso: senza ? e senza #. Pages confronta il percorso, non i parametri.',
      fromDoubleSlash: 'L’indirizzo vecchio contiene //: controlla che non manchi un pezzo.',
      fromTooLong: 'L’indirizzo vecchio è lungo al massimo 1.000 caratteri.',
      toMissing: 'Scrivi l’indirizzo nuovo.',
      toInvalid: 'L’indirizzo nuovo è un percorso che inizia con / oppure un indirizzo completo che inizia con https://.',
      toSpaces: 'L’indirizzo nuovo non può contenere spazi: scrivili come %20.',
      toTooLong: 'L’indirizzo nuovo è lungo al massimo 2.000 caratteri.',
      self: 'Un indirizzo non può portare a sé stesso.',
      status: 'Il tipo è 301 (permanente) o 302 (temporaneo).',
      loop: '{{to}} porta già a {{from}}: salvando si creerebbe un giro senza uscita.',
    },
  },
}

export const libraryEn: DictNode = {
  media: {
    title: 'Media library',
    subtitle: 'Images, PDFs and videos of {{site}}: upload them once, use them in every content.',
    search: 'Search the library',
    searchPlaceholder: 'Search by name or path',
    filters: { kind: 'File type', status: 'Status' },
    kinds: {
      all: 'All',
      image: 'Images',
      document: 'Documents',
      video: 'Videos',
      imageOne: 'Image',
      documentOne: 'Document',
      videoOne: 'Video',
    },
    statuses: {
      active: 'In use',
      archived: 'Archived',
      all: 'All',
      activeOne: 'In use',
      archivedOne: 'Archived',
    },
    loadError: 'Cannot read the library',
    retry: 'Try again',
    empty: 'The library is empty',
    emptyHelp: 'Drop images above or press “Choose files”. Then you find them in the image field of every content.',
    emptyReadOnly: 'No file uploaded for this site. Your role can see the library but not upload.',
    emptyFiltered: 'No file matches',
    emptyFilteredHelp: 'Try another word or clear the filters.',
    clearFilters: 'Clear filters',
    count: '{{count}} files, {{size}} in total',
    noAlt: 'Alt text missing',
    upload: {
      drop: 'Drop files here to upload',
      limits: 'PNG, JPG, GIF, WebP, SVG, PDF and MP4 up to 10 MB. Several at once.',
      choose: 'Choose files',
      fileNamePlaceholder: 'file name',
      progress: 'Uploading {{name}}',
      waiting: 'Queued',
      duplicate: 'Already in the library: using that one, {{path}}',
      clear: 'Clear the upload list',
      problems: {
        empty: 'The file is empty.',
        tooLarge: 'It weighs {{size}}: the limit is 10 MB. Shrink it (for example to WebP) and try again.',
        unsupported: 'This file type cannot be uploaded: the library takes PNG, JPG, GIF, WebP, SVG, PDF and MP4.',
      },
    },
    reason: {
      upload: 'Uploaded “{{name}}” to the library from Migliore CMS',
      uploadForField: 'Uploaded “{{name}}” for the field “{{field}}” from Migliore CMS',
      update: 'Updated the library file “{{name}}” from Migliore CMS',
      archive: 'Archived the library file “{{name}}” from Migliore CMS',
      restore: 'Restored the library file “{{name}}” from Migliore CMS',
    },
    detail: {
      title: 'Library file',
      loadError: 'Cannot open the file',
      saved: 'File updated',
      archived: 'File archived: it stays out of the next publication',
      restored: 'File restored',
      nameRequired: 'The name cannot be empty.',
      copied: 'Copied: {{text}}',
      copyFailed: 'The browser did not allow copying: select the address and copy it by hand.',
      copyPath: 'Copy path',
      copyUrl: 'Copy public address',
      openFile: 'Open the file',
      status: 'Status',
      publicPath: 'Path on the site',
      type: 'Type',
      dimensions: 'Dimensions',
      size: 'Size',
      version: 'Version',
      notStored: 'This file was recorded before the library kept files: it only exists on the published site. The preview comes from there.',
      archivedTitle: 'Archived file',
      archivedHelp: 'It no longer appears in the library nor in the next publication. The file is kept: restore it to use it again.',
      name: 'Name',
      path: 'Path',
      pathHelp: 'Under media/, with the same extension. It is the address pages use.',
      pathChangeWarning: 'Careful: pages using the current path must be changed too, or they show a missing image.',
      alt: 'Alt text',
      altHelp: 'Describes the image to people who cannot see it and to search engines. One short sentence per site language.',
      altFor: 'Alt text ({{locale}})',
      archive: 'Archive',
      archiveTitle: 'Archive the file?',
      archiveMessage: 'The file {{path}} leaves the library and the next publication. Pages using it will show a missing image. It can be restored.',
      restore: 'Restore',
    },
    picker: {
      title: 'Choose from the library',
      titleFor: 'Choose a file for “{{field}}”',
      hint: 'Tap a file to select it, double click to use it right away.',
      selected: 'Selected: {{name}}',
      use: 'Use this file',
    },
    field: {
      choose: 'Choose from the library',
      upload: 'Upload',
      clear: 'Remove the file',
      archived: 'archived: it will not be published',
      outsideLibrary: 'This address is not in the library: that is fine, but the preview depends on the published site.',
    },
  },
  schedule: {
    title: 'Publication calendar',
    subtitle: 'Published contents with a future date. On that date the site republishes by itself, once.',
    openContents: 'Published contents',
    loadError: 'Cannot read the calendar',
    deliveryOffTitle: 'Delivery of the site is off',
    deliveryOff: 'Nothing happens on the date: contents stay out until you turn it back on in the settings.',
    openSettings: 'Open settings',
    next: 'Next release: {{when}} ({{relative}})',
    nextHelp: 'At that time the CMS republishes {{domain}} with the new content, without anyone pressing “Publish site”.',
    truncated: 'Showing the first {{shown}} releases of {{total}}.',
    views: 'Calendar views',
    week: 'Week',
    list: 'List',
    empty: 'Nothing scheduled',
    emptyHelp: 'Open a content, pick a future date in the “Scheduling” panel and mark it “Published”: it will show here.',
    weekOf: '{{from}} – {{to}}',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    today: 'Today',
    nothing: 'Nothing',
    otherWeeks: '{{count}} more releases in other weeks.',
    goToNext: 'Go to the next one',
    entryMeta: 'At {{time}} · {{locale}} · /{{slug}}',
    reason: {
      set: 'Publication date set to {{date}} from Migliore CMS',
      clear: 'Publication date removed from Migliore CMS',
    },
    card: {
      title: 'Scheduling',
      date: 'Publication date and time',
      help: 'In your time zone. A future date keeps the content off the site until then.',
      blocked: 'Save the other changes first: the date is saved on its own.',
      none: 'No date: the content goes out with the next publication of the site.',
      scheduled: 'Goes out {{when}}',
      past: 'Publication date: {{when}}.',
      waiting: 'Future date, but not “Published” yet',
      waitingHelp: 'Mark the content “Published”: it goes out on the chosen date, not before.',
      save: 'Save date',
      clear: 'Remove date',
      saved: 'Date saved',
      cleared: 'Date removed',
      conflict: 'The content changed meanwhile: I reloaded it. Your date is still here, press “Save date” again.',
      openCalendar: 'Open the calendar',
    },
  },
  redirects: {
    title: 'Redirects',
    subtitle: 'Old addresses leading to new ones, so visits and rankings are not lost. They apply from the next publication of the site.',
    new: 'New redirect',
    countStatic: '{{count}} of {{max}} fixed',
    countDynamic: '{{count}} of {{max}} with placeholders',
    publishHint: 'Cloudflare Pages limits.',
    nearLimitTitle: 'Close to the Cloudflare Pages limit',
    nearLimit: '{{staticLeft}} fixed and {{dynamicLeft}} placeholder redirects left. Beyond that Pages would ignore the rest: remove the ones nobody visits any more.',
    collapsedTitle: 'Chain collapsed',
    collapsed: '{{typed}} was itself a redirect: {{from}} now leads straight to {{stored}}, in one hop.',
    loadError: 'Cannot read the redirects',
    search: 'Search redirects',
    searchPlaceholder: 'Search an old or new address',
    empty: 'No redirects',
    emptyHelp: 'When an address changes, add the old and the new one here: people coming from Google or a saved link find the right page.',
    emptyFiltered: 'No redirect contains “{{q}}”',
    emptyFilteredHelp: 'The search looks at both the old and the new address.',
    clearSearch: 'Clear the search',
    status: {
      301: '301 permanent',
      302: '302 temporary',
    },
    statusHelp: {
      301: 'Forever: search engines pass the ranking to the new address. Almost always the right choice.',
      302: 'For a while: search engines keep the old address. For campaigns and temporary pages.',
    },
    dynamic: 'With placeholders',
    dynamicHelp: 'With * or :name it covers many addresses at once (for example /blog/* → /articles/:splat). Pages takes at most 100.',
    edit: 'Edit',
    actionsFor: 'Actions for {{from}}',
    tryOld: 'Try the old address',
    tryOldHelp: 'On the published site',
    remove: 'Remove',
    removeTitle: 'Remove the redirect?',
    removeMessage: 'After the next publication {{from}} will no longer lead to {{to}}: whoever opens it will find a missing page.',
    wwwNote: 'Going from www to the main domain is not set here: it is a rule of the domain on Cloudflare.',
    newTitle: 'New redirect',
    editTitle: 'Edit redirect',
    refused: 'The redirect was not saved',
    from: 'Old address',
    fromHelp: 'The path that no longer exists, without the domain: /old-article. You can paste the full address, only the path is kept.',
    fromChanged: 'You are changing the old address: {{old}} will be removed and replaced by this one.',
    fromTaken: 'A redirect from this address already exists: saving replaces it.',
    to: 'New address',
    toHelp: 'A path of the site (/new-article) or a full address https://…',
    type: 'Type',
    chainHelp: 'If the new address is itself a redirect, the CMS stores the final destination directly. A loop back to the start is refused.',
    added: 'Redirect from {{from}} added',
    updated: 'Redirect from {{from}} updated',
    removed: 'Redirect from {{from}} removed',
    reason: {
      add: 'Added the redirect {{from}} → {{to}} from Migliore CMS',
      update: 'Updated the redirect {{from}} → {{to}} from Migliore CMS',
      remove: 'Removed the redirect from {{from}} from Migliore CMS',
    },
    problems: {
      fromMissing: 'Write the old address.',
      fromHome: 'The home page cannot be redirected: every other address would lose its way back.',
      fromSpaces: 'The old address cannot contain spaces.',
      fromQuery: 'Only the path: no ? and no #. Pages matches the path, not the parameters.',
      fromDoubleSlash: 'The old address contains //: check that nothing is missing.',
      fromTooLong: 'The old address is at most 1,000 characters.',
      toMissing: 'Write the new address.',
      toInvalid: 'The new address is a path starting with / or a full address starting with https://.',
      toSpaces: 'The new address cannot contain spaces: write them as %20.',
      toTooLong: 'The new address is at most 2,000 characters.',
      self: 'An address cannot lead to itself.',
      status: 'The type is 301 (permanent) or 302 (temporary).',
      loop: '{{to}} already leads to {{from}}: saving would create a loop with no way out.',
    },
  },
}
